// The account's own surfaces over the room: the menu under the avatar, the
// paywall where a plan is chosen, and the sign-in dialog a guest reaches from
// either. They read the account through the same typed contract the
// simulation tests drive (src/account/client.ts).

import { track } from "../api/events.ts";
import { LEGAL, PRIVACY_PATH, TERMS_PATH } from "../core/legal.ts";
import { DOCS_PATH, HELP_PATH } from "../core/install-guide.ts";
import type { BrowserPlace } from "../account/client.ts";
import type { AccountState, Plan } from "../account/contract.ts";
import { PLANS } from "../account/contract.ts";
import { MEMBERSHIP_SIGNUPS_OPEN } from "../core/membership.ts";
import { mainlandZone } from "../core/seller.ts";
import { PERKS, perkSvg, perkText, soonText, type Perk } from "../core/perks.ts";
import { formatDay, formatPrice, type MessageKey } from "../core/i18n.ts";
import { lang, localized, t } from "./lang.ts";
import { rich } from "./names.ts";
import type { AuthProvidersResponse } from "../core/protocol.ts";
import type { MembershipInfo, UserInfo } from "../core/types.ts";
import type { OfferState } from "../core/offers.ts";
import { linkWith } from "../api/auth.ts";
import { h } from "./dom.ts";
import { createSignIn, langSwitch, type SignInOptions } from "./signin.ts";

export interface AccountActions {
  /** The account as the server holds it. Rejects where there is no accounts system. */
  state(): Promise<AccountState>;
  providers(): Promise<AuthProvidersResponse>;
  /** The payment page for a plan. `place` is what the browser knows of where the buyer is. */
  checkout(plan: Plan, place?: BrowserPlace): Promise<string>;
  /** The page where a member changes or cancels the plan. */
  portal(): Promise<string>;
  /** Take a handle for the account. Resolves with the handle it now has, or with the refusal to show. */
  setHandle(handle: string): Promise<{ handle: string } | { error: string }>;
  signOut(): void;
  toast(message: string): void;
}

/** What the visitor reached for: a package, a colour, or the membership itself. */
export type PaywallReason = "download" | "colours" | "member";

const TITLES: Record<PaywallReason, MessageKey> = {
  download: "paywall.download",
  colours: "paywall.colours",
  member: "account.join",
  // Nothing is promised for today: the list below says the benefit is to come.
};

/** The query parameter that reopens the paywall on a plan after a sign-in that left the page. */
export const JOIN_PARAM = "join";

const day = (time: number) => formatDay(lang(), time);
const price = (amount: number, currency?: "jpy") => formatPrice(lang(), amount, currency);

/** One line that says where the account stands with the membership. */
export function planLine(membership: MembershipInfo): string {
  if (!membership.active) return t("plan.free");
  // A membership an offer gave outlasts a paid one only when it is for life or ends later.
  if (membership.grant && (membership.grant.until === null || membership.grant.until >= (membership.paidUntil ?? 0))) {
    return membership.grant.until === null ? t("plan.life") : t("plan.until", { day: day(membership.grant.until) });
  }
  if (!membership.paidUntil) return t("plan.member");
  return membership.cancelAtPeriodEnd ? t("plan.until", { day: day(membership.paidUntil) }) : t("plan.renews", { day: day(membership.paidUntil) });
}

/**
 * A layer over the room that holds one card and the keyboard: Escape and a
 * press outside leave, Tab stays inside, and no key reaches the room.
 */
function dialog(root: HTMLElement, label: string, content: HTMLElement, onClose?: () => void): { element: HTMLElement; close(): void } {
  root.querySelector(".paywall")?.remove();
  const back = document.activeElement as HTMLElement | null;
  const close = () => {
    element.remove();
    back?.focus?.();
    onClose?.();
  };
  const element = h("div", { class: "paywall", role: "dialog", "aria-modal": "true", "aria-label": label, onclick: (event) => {
    if (event.target === element) close();
  } }, content);
  element.addEventListener("keydown", (event) => {
    event.stopPropagation();
    if (event.key === "Escape") close();
    if (event.key !== "Tab") return;
    const stops = [...element.querySelectorAll<HTMLElement>("button, input, a[href]")].filter((stop) => !(stop as HTMLButtonElement).disabled);
    if (stops.length === 0) return;
    const at = stops.indexOf(document.activeElement as HTMLElement);
    event.preventDefault();
    stops[(at + (event.shiftKey ? -1 : 1) + stops.length) % stops.length]!.focus();
  });
  element.addEventListener("keyup", (event) => event.stopPropagation());
  // A text field keeps its keys to itself (signin.ts), Escape among them: the card hears that one first.
  element.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    event.stopPropagation();
    close();
  }, true);
  root.append(element);
  return { element, close };
}

/**
 * The sign-in card as a dialog, for a guest who wants an account. `onDone`
 * runs once signed in, `onLeave` when the guest closes it instead. `why` is
 * what the guest reached for, for the event log.
 */
export function openSignIn(root: HTMLElement, actions: AccountActions, lead: string, onDone: () => void, why: SignInOptions["why"] = "menu", onLeave?: () => void): void {
  void actions.providers().then((providers) => {
    const card = createSignIn({ providers, title: t("signin.title"), lead, onDone, why });
    dialog(root, t("signin.title"), card, onLeave);
    card.querySelector<HTMLElement>("input, button")?.focus();
  }, () => {
    actions.toast(t("signin.unavailable"));
    onLeave?.();
  });
}

/** One benefit in a list: its picture, its text, and whether it is still to come. */
function perk(entry: Perk): HTMLElement {
  const picture = h("span", { class: "perk-icon", "aria-hidden": "true" });
  // The pictures are this repository's own drawings (core/perks.ts), not a visitor's text.
  picture.innerHTML = perkSvg(entry.id);
  return h("li", {}, picture, h("span", {}, perkText(entry.id, lang()), entry.soon ? h("small", { text: soonText(lang()) }) : null));
}

/**
 * Asks an account once to accept the current Terms and Privacy Policy: one
 * that never agreed, or agreed to an earlier version. `accept` records it.
 */
export function openAgreement(root: HTMLElement, accept: () => Promise<void>, signOut: () => void): void {
  const note = h("p", { class: "signin-note", role: "alert" });
  const go = h("button", { class: "btn", type: "button" }, t("agreement.go"));
  go.addEventListener("click", () => {
    go.disabled = true;
    note.textContent = "";
    accept().then(() => box.close(), () => {
      go.disabled = false;
      note.textContent = t("agreement.failed");
    });
  });
  const card = h("div", { class: "paywall-card agreement" },
    h("h2", { text: t("agreement.title") }),
    h("p", {}, ...rich("agreement.body", {
      terms: h("a", { href: localized(TERMS_PATH), target: "_blank", rel: "noopener" }, t("legal.terms")),
      privacy: h("a", { href: localized(PRIVACY_PATH), target: "_blank", rel: "noopener" }, t("legal.privacy")),
    })),
    go,
    note,
    h("button", { class: "quiet", type: "button", onclick: () => signOut() }, t("agreement.signOut")));
  const box = dialog(root, t("agreement.title"), card);
  go.focus();
}

/**
 * The paywall: what the membership gives, the two ways to pay for it, and
 * the way on to payment. A guest signs in first; the plan survives the trip.
 */
export function openPaywall(root: HTMLElement, actions: AccountActions, membership: MembershipInfo, reason: PaywallReason, chosen: Plan = "year"): void {
  if (!MEMBERSHIP_SIGNUPS_OPEN) return;
  let plan: Plan = chosen;
  const yearly = (membership.annualPrice ?? PLANS.year.amount) / 12;
  const option = (id: Plan, title: string, detail: string) => {
    const input = h("input", { type: "radio", name: "plan", id: `plan-${id}` });
    input.checked = id === plan;
    input.addEventListener("change", () => (plan = id));
    return h("label", { class: "plan" }, input, h("span", { class: "plan-text" }, h("b", { text: title }), h("small", { text: detail })));
  };
  const note = h("p", { class: "signin-note", role: "alert" });
  const go = h("button", { class: "btn", type: "button" }, t("paywall.go"));

  // Where the buyer is, as far as the browser can say. Link, which sells the membership elsewhere, does not
  // sell to a buyer in mainland China: the server gives that buyer the direct checkout (sellerFor).
  let timeZone: string | undefined;
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    // (a browser that cannot say gets the checkout its network address decides)
  }
  // A buyer in mainland China whom neither the network address nor the time zone shows, behind a proxy with
  // the clock set elsewhere, says so here. Offered where the browser reads Chinese and the zone did not say it.
  const askMainland = LEGAL.merchantOfRecord && !mainlandZone(timeZone) && (navigator.languages ?? [navigator.language]).some((tag) => /^zh\b/i.test(tag ?? ""));

  const pay = async (mainland = false) => {
    go.disabled = true;
    note.textContent = "";
    try {
      const state = await actions.state();
      if (!state.user) {
        // The address keeps the plan, so a sign-in that leaves for Google or
        // GitHub comes back to this choice.
        const url = new URL(location.href);
        url.searchParams.set(JOIN_PARAM, plan);
        history.replaceState(null, "", url);
        box.close();
        openSignIn(root, actions, t("paywall.signIn"), () => location.reload(), "paywall");
        return;
      }
      if (!state.billingReady) {
        note.textContent = t("paywall.closed");
        return;
      }
      track("paywall.plan", { props: { plan, reason } });
      location.assign(await actions.checkout(plan, { timeZone, mainland }));
    } catch {
      note.textContent = t("paywall.failed");
    } finally {
      go.disabled = false;
    }
  };
  go.addEventListener("click", () => void pay());

  const card = h("div", { class: "paywall-card" },
    h("h2", { text: t(TITLES[reason]) }),
    h("ul", { class: "perks" }, ...PERKS.map(perk)),
    h("div", { class: "plans", role: "radiogroup", "aria-label": t("paywall.plan") },
      option("year", t("paywall.perMonth", { price: price(Math.round(yearly), membership.currency) }), t("paywall.yearly", { price: price(membership.annualPrice ?? PLANS.year.amount, membership.currency) })),
      option("month", t("paywall.perMonth", { price: price(membership.price, membership.currency) }), t("paywall.monthly"))),
    h("p", { class: "paywall-terms" }, ...rich("paywall.terms", {
      terms: h("a", { href: localized(TERMS_PATH), target: "_blank", rel: "noopener" }, t("legal.terms")),
      privacy: h("a", { href: localized(PRIVACY_PATH), target: "_blank", rel: "noopener" }, t("legal.privacy")),
    })),
    go,
    note,
    askMainland ? h("button", { class: "quiet", type: "button", onclick: () => void pay(true) }, t("paywall.mainland")) : null,
    h("button", { class: "quiet", type: "button", onclick: () => box.close() }, t("paywall.notNow")));
  const box = dialog(root, t(TITLES[reason]), card);
  go.focus();
  track("paywall.shown", { props: { reason } });
}

/**
 * The account's handle, and the way to choose another: one row that says
 * what it is, and a quiet button that turns into a field in its place. A
 * refusal is shown under the field and the field stays.
 */
function handleControl(actions: AccountActions, current: string): HTMLElement {
  const value = h("b", { text: current });
  const row = h("div", { class: "account-handle" }, h("span", { text: t("account.handle") }), value);
  const box = h("div", { class: "account-handle-box" }, row);
  const closed = (): void => {
    const change = h("button", { class: "quiet start", type: "button", onclick: () => open() }, t("account.handle.change"));
    box.replaceChildren(row, change);
  };
  const open = (): void => {
    const input = h("input", {
      class: "field slug", type: "text", name: "handle", maxlength: "25", spellcheck: "false", autocapitalize: "off", autocomplete: "off",
      "aria-label": t("account.handle"),
    });
    input.value = value.textContent ?? "";
    const save = h("button", { class: "copy", type: "submit", text: t("room.save") });
    const note = h("p", { class: "signin-note", role: "alert" });
    const form = h("form", { class: "rename" }, input, save);
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      const wanted = input.value.trim();
      if (wanted === value.textContent) return closed();
      save.disabled = true;
      void actions.setHandle(wanted).then((result) => {
        save.disabled = false;
        if ("error" in result) {
          note.textContent = result.error;
          input.focus();
          return;
        }
        value.textContent = result.handle;
        closed();
        box.querySelector<HTMLElement>("button")?.focus();
      });
    });
    box.replaceChildren(row, form, note, h("small", { text: t("account.handle.rule") }));
    input.focus();
    input.select();
  };
  closed();
  return box;
}

/**
 * The page that claims an offer (src/core/offers.ts): what the offer gives,
 * where this account stands with it, and the one thing to do next. No part
 * of the Studio links to it; its address is `/studio/?offer=<id>`, which
 * stays in the address through a sign-in and through linking GitHub.
 * `onClaimed` runs once the membership is granted.
 */
export function openOffer(root: HTMLElement, actions: AccountActions, id: string, onClaimed: () => void): void {
  const card = h("div", { class: "paywall-card offer-card" });
  const box = dialog(root, t("offer.label"), card, () => leave());
  const leave = () => {
    const url = new URL(location.href);
    url.searchParams.delete("offer");
    history.replaceState(history.state, "", url);
  };
  const note = h("p", { class: "signin-note", role: "alert" });
  const done = () => h("button", { class: "quiet", type: "button", onclick: () => box.close() }, t("offer.done"));
  const path = `/api/offers/${encodeURIComponent(id)}`;
  // What a refused GitHub link came back with (src/api/auth.ts linkWith); read once and taken out of the address.
  const back = new URL(location.href);
  const refused = back.searchParams.get("error");
  if (refused) {
    back.searchParams.delete("error");
    back.searchParams.delete("error_description");
    history.replaceState(history.state, "", back);
  }

  const show = (state: OfferState) => {
    const gives = state.offer.days === null ? t("offer.gives.life") : t("offer.gives.days", { days: state.offer.days });
    const parts: HTMLElement[] = [h("h2", { text: state.offer.title }), h("p", { class: "lead", text: gives })];
    const claim = async (code?: string) => {
      note.textContent = "";
      try {
        const reply = await fetch(path, { method: "POST", credentials: "same-origin", headers: { "content-type": "application/json" }, body: JSON.stringify(code === undefined ? {} : { code }) });
        if (reply.status === 429) {
          note.textContent = t("signin.tooMany");
          return;
        }
        if (!reply.ok) throw new Error(String(reply.status));
        const next = (await reply.json()) as OfferState;
        // A wrong code keeps the field, to try again.
        if (next.state === "wrong-code") {
          note.textContent = t("offer.wrongCode");
          card.querySelector<HTMLInputElement>("input")?.select();
          return;
        }
        if (next.state === "granted") onClaimed();
        show(next);
      } catch {
        note.textContent = t("offer.failed");
      }
    };
    switch (state.state) {
      case "sign-in":
        box.close();
        openSignIn(root, actions, t("offer.signIn"), () => location.reload(), "other");
        return;
      case "granted":
        leave();
        parts.push(h("p", { class: "lead", text: state.until ? t("offer.granted.until", { day: day(state.until) }) : t("offer.granted.life") }), done());
        break;
      case "ready":
        if (state.offer.proof === "github") {
          parts.push(h("p", { text: t("offer.ready.github") }), h("button", { class: "btn", type: "button", onclick: () => void claim() }, t("offer.claim")), note);
        } else {
          const field = h("input", { class: "field offer-field", type: "text", autocomplete: "off", spellcheck: "false", autocapitalize: "characters", maxlength: "32", "aria-label": t("offer.code"), placeholder: "XXXXX-XXXXX-XXXXX" });
          for (const type of ["keydown", "keyup"] as const) field.addEventListener(type, (event) => event.stopPropagation());
          const form = h("form", { class: "offer-code" }, field, h("button", { class: "btn", type: "submit" }, t("offer.claim")));
          form.addEventListener("submit", (event) => {
            event.preventDefault();
            void claim(field.value);
          });
          parts.push(h("p", { text: t("offer.ready.code") }), form, note);
        }
        break;
      case "connect-github": {
        const link = h("button", { class: "btn", type: "button" }, t("offer.connect.button"));
        link.addEventListener("click", async () => {
          link.disabled = true;
          note.textContent = "";
          try {
            // Better Auth links the GitHub account to the signed-in one and comes back to this address, offer and all.
            await linkWith("github");
          } catch {
            link.disabled = false;
            note.textContent = t("offer.linkFailed");
          }
        });
        // A link that came back refused says why once.
        if (refused) note.textContent = refused === "account_already_linked_to_different_user" ? t("offer.linkedElsewhere") : t("offer.linkFailed");
        parts.push(h("p", { text: t("offer.connect") }), link, note);
        break;
      }
      case "not-listed":
        parts.push(h("p", { text: t("offer.notListed") }), done());
        break;
      case "used":
        parts.push(h("p", { text: t("offer.used") }), done());
        break;
      case "closed":
        leave();
        parts.push(h("p", { text: t("offer.closed") }), done());
        break;
    }
    card.replaceChildren(...parts);
    card.querySelector<HTMLElement>("input, .btn")?.focus();
  };

  card.replaceChildren(h("p", { class: "lead", text: t("room.loading") }));
  fetch(path, { credentials: "same-origin" })
    .then(async (reply) => {
      if (reply.status === 404) {
        leave();
        card.replaceChildren(h("h2", { text: t("offer.label") }), h("p", { class: "lead", text: t("offer.missing") }), done());
        return;
      }
      if (!reply.ok) throw new Error(String(reply.status));
      show((await reply.json()) as OfferState);
    })
    .catch(() => card.replaceChildren(h("h2", { text: t("offer.label") }), h("p", { class: "lead", text: t("offer.failed") }), done()));
}

/** The menu under the avatar: who is signed in, the plan, and what can be done about either. */
export function openAccountMenu(
  root: HTMLElement, anchor: HTMLElement, actions: AccountActions, user: UserInfo, membership: MembershipInfo, onJoin: () => void,
): void {
  const existing = root.querySelector(".account-menu");
  if (existing) {
    existing.remove();
    return;
  }
  const who = h("small", { text: user.guest ? t("account.guest") : `@${user.handle}` });
  const menu = h("div", { class: "account-menu", role: "dialog", "aria-label": t("room.account") },
    h("div", { class: "account-who" }, h("b", { text: user.name || user.handle }), who),
    h("div", { class: "account-plan" }, h("span", { text: t("account.plan") }), h("b", { text: planLine(membership) })));
  // A guest's handle is made for it and goes when the guest signs in.
  if (!user.guest) menu.append(handleControl(actions, user.handle));
  const close = () => {
    menu.remove();
    document.removeEventListener("pointerdown", outside, true);
  };
  const outside = (event: Event) => {
    if (!menu.contains(event.target as Node) && event.target !== anchor) close();
  };
  const item = (label: string, cls: string, run: () => void) =>
    h("button", { class: cls, type: "button", onclick: () => {
      close();
      run();
    } }, label);

  // A membership an offer gave has no subscription to manage; one that is also paid for does.
  if (membership.active && (membership.paidUntil || !membership.grant)) {
    if (membership.grant?.until === null && !membership.cancelAtPeriodEnd) menu.append(h("p", { class: "account-note", text: t("account.grantRenews") }));
    menu.append(item(t("account.manage"), "btn ghost", () => {
      actions.portal().then((url) => location.assign(url), () => actions.toast(t("account.portalFailed")));
    }));
  } else if (membership.active) {
    // (a membership an offer gave: nothing to join or to manage)
  } else if (MEMBERSHIP_SIGNUPS_OPEN) {
    menu.append(item(t("account.join"), "btn", onJoin));
  }
  if (user.guest) {
    menu.append(item(t("signin.title"), "btn ghost", () =>
      openSignIn(root, actions, t("account.keep"), () => location.reload())));
  }
  menu.append(item(user.guest ? t("account.leave") : t("account.signOut"), "quiet", () => actions.signOut()));
  menu.append(h("p", { class: "account-legal" },
    h("a", { href: localized(DOCS_PATH), target: "_blank", rel: "noopener" }, t("foot.docs")),
    h("a", { href: "/oauth/connected", target: "_blank", rel: "noopener" }, t("oauth.connected")),
    h("a", { href: localized(HELP_PATH), target: "_blank", rel: "noopener" }, t("guide.title")),
    h("a", { href: localized(TERMS_PATH), target: "_blank", rel: "noopener" }, t("foot.terms")),
    h("a", { href: localized(PRIVACY_PATH), target: "_blank", rel: "noopener" }, t("foot.privacy"))));
  menu.append(langSwitch());

  menu.addEventListener("keydown", (event) => {
    event.stopPropagation();
    if (event.key === "Escape") {
      close();
      anchor.focus();
    }
  });
  root.append(menu);
  document.addEventListener("pointerdown", outside, true);
  // The email is the account's, not the room's: it is read when the menu opens.
  void actions.state().then((state) => {
    if (state.user?.email) who.textContent = state.user.email;
  }, () => null);
  menu.querySelector<HTMLElement>("button")?.focus();
}
